<template>
  <router-view v-slot="{ Component, route }">
    <transition
      :name="transitionName"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <component :is="Component" :key="route.path" />
    </transition>
  </router-view>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      transitionName: 'page-fade-blur'
    }
  },
  watch: {
    '$route'(to, from) {
      // 根据路由变化决定过渡效果名称
      // 可以根据不同路由设置不同的过渡效果
      this.transitionName = 'page-fade-blur'
    }
  },
  methods: {
    beforeEnter(el) {
      // 进入前：设置初始状态 - 更柔和的起始状态
      el.style.opacity = '0'
      el.style.filter = 'blur(10px) brightness(0.95)'
      el.style.transform = 'translateY(15px) scale(0.97)'
      el.style.willChange = 'opacity, filter, transform'
    },
    enter(el, done) {
      // 进入动画 - 使用更自然的缓动曲线和协调的时间
      // 透明度先快速变化，然后缓慢完成
      // 模糊效果稍慢一些，让渐变更自然
      // 位移和缩放同步，但使用不同的缓动
      const opacityDuration = 700
      const blurDuration = 850
      const transformDuration = 750
      
      // 使用更自然的缓动函数组合
      // opacity: 快速开始，缓慢结束
      // filter: 更慢的渐变，让模糊消失更自然
      // transform: 平滑的弹性效果
      el.style.transition = `
        opacity ${opacityDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
        filter ${blurDuration}ms cubic-bezier(0.33, 1, 0.68, 1),
        transform ${transformDuration}ms cubic-bezier(0.34, 1.56, 0.64, 1)
      `
      
      // 使用双 requestAnimationFrame 确保浏览器已准备好渲染
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.opacity = '1'
          el.style.filter = 'blur(0px) brightness(1)'
          el.style.transform = 'translateY(0px) scale(1)'
          setTimeout(() => {
            el.style.willChange = 'auto'
            done()
          }, Math.max(opacityDuration, blurDuration, transformDuration))
        })
      })
    },
    afterEnter(el) {
      // 进入后：清理样式，恢复性能
      el.style.transition = ''
      el.style.willChange = 'auto'
    },
    beforeLeave(el) {
      // 离开前：准备离开状态
      el.style.opacity = '1'
      el.style.filter = 'blur(0px) brightness(1)'
      el.style.transform = 'translateY(0px) scale(1)'
      el.style.willChange = 'opacity, filter, transform'
    },
    leave(el, done) {
      // 离开动画 - 更自然的淡出效果
      const opacityDuration = 550
      const blurDuration = 600
      const transformDuration = 550
      
      // 离开时使用不同的缓动，让淡出更自然
      // opacity: 快速淡出
      // filter: 模糊逐渐增加
      // transform: 轻微上移和缩小
      el.style.transition = `
        opacity ${opacityDuration}ms cubic-bezier(0.55, 0.06, 0.68, 0.19),
        filter ${blurDuration}ms cubic-bezier(0.4, 0, 0.2, 1),
        transform ${transformDuration}ms cubic-bezier(0.55, 0.06, 0.68, 0.19)
      `
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.opacity = '0'
          el.style.filter = 'blur(10px) brightness(0.9)'
          el.style.transform = 'translateY(-15px) scale(0.97)'
          setTimeout(() => {
            el.style.willChange = 'auto'
            done()
          }, Math.max(opacityDuration, blurDuration, transformDuration))
        })
      })
    },
    afterLeave(el) {
      // 离开后：清理样式
      el.style.transition = ''
      el.style.willChange = 'auto'
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  background-color: #0a192f;
  transition: background-color 0.3s ease;
  /* 移动端优化 */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
}

/* 允许输入框和文本区域选择文本 */
input, textarea, [contenteditable] {
  -webkit-user-select: text;
  user-select: text;
}

#app {
  width: 100%;
  height: 100vh;
  height: 100dvh; /* 动态视口高度，适配移动端浏览器 */
  position: relative;
  overflow: hidden;
  background-color: #0a192f;
  transition: background-color 0.3s ease;
  /* 移动端优化 */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* 移动端全局优化 */
@media (max-width: 768px) {
  body {
    font-size: 14px;
    line-height: 1.5;
  }
  
  #app {
    height: 100vh;
    height: 100dvh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  /* 确保所有按钮和可点击元素有足够的触摸目标 */
  button, a, [role="button"], .btn, input[type="button"], input[type="submit"] {
    min-height: 44px;
    min-width: 44px;
    touch-action: manipulation;
  }
  
  /* 优化输入框在移动端的显示 */
  input, textarea, select {
    font-size: 16px !important; /* 防止iOS自动缩放 */
    -webkit-appearance: none;
    appearance: none;
    border-radius: 0; /* iOS Safari 默认样式 */
  }
  
  /* 优化滚动条 */
  * {
    -webkit-overflow-scrolling: touch;
  }
  
  /* 禁用长按选择（除了输入框） */
  *:not(input):not(textarea):not([contenteditable]) {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
}

/* 小屏手机优化 */
@media (max-width: 480px) {
  body {
    font-size: 13px;
  }
}

/* 页面切换过渡效果 - 重叠过渡，无白边 */
.page-fade-blur-enter-active,
.page-fade-blur-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: opacity, filter, transform;
  backface-visibility: hidden;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  overflow: hidden;
}

/* 新页面在上层 */
.page-fade-blur-enter-active {
  z-index: 2;
}

/* 旧页面在下层 */
.page-fade-blur-leave-active {
  z-index: 1;
}

/* 确保所有页面组件都能正确应用过渡 */
.page-fade-blur-enter-active > *,
.page-fade-blur-leave-active > * {
  width: 100%;
  height: 100%;
  position: relative;
}

/* 添加过渡背景层，确保没有白色间隙 - 使用深色背景覆盖 */
.page-fade-blur-enter-active::before,
.page-fade-blur-leave-active::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #0a192f;
  z-index: 0;
  pointer-events: none;
  opacity: 1;
}
</style>
